<i18n>
{
	"en": {
		"deleteItem": "Remove item | Remove item | Remove items",
		"noItemsFound": "No Items...",
		"header": "Unknown Items",
		"barcode": "Registered Barcode",
		"message": "Message",
		"pictures": "Photos",
		"registeredDate": "Registered Date",
		"editedDate": "Edited Date",
		"failedToFetch": "Failed to get Unknown Items",
		"failedToDelete": "Failed to delte item",
		"failedToAdd": "Failed to add item",
		"deletedItems": "Deleted item | Deleted item | Deleted {count} items"
	},
	"no": {
		"deleteItem": "Fjern pakke | Fjern pakke | Fjern pakker",
		"noItemsFound": "Ingen pakker...",
		"header": "Ukjente pakker",
		"barcode": "Registrert strekkode",
		"message": "Melding",
		"pictures": "Bilder",
		"registeredDate": "Registrert dato",
		"editedDate": "Endret dato",
		"failedToFetch": "Kunne ikke hente ukjente pakker",
		"failedToDelete": "Kunne ikke slette pakke",
		"failedToAdd": "Kunne ikke legge til pakke",
		"deletedItems": "Pakken ble slettet | Pakken ble slettet | Slettet {count} pakker"
	}
}
</i18n>
<template>
	<t-vertical-panel class="unknown-list">
		<div slot="header" class="unknown-list-header">
			<t-panel-header size="md" icon="orders">
				{{ $t('header', itemsFound) }}
			</t-panel-header>
		</div>
		<div slot="sub-header" class="unknown-list-sub-header">
			<t-button
				:disabled="rowsSelected == 0"
				iconBefore="trash"
				appearance="minimal"
				width="150px"
				type="bp"
				@click="onDeleteItems"
			>{{$tc('deleteItem', rowsSelected)}}</t-button>
		</div>
		<div class="unknown-list-content">
			<t-grid
				v-if="itemsFound"
				ref="grid"
				:columnDefinitions="columnDefinitions"
				:defaultColumnDefinition="defaultColumnDefinition"
				:rowHeight="115"
				:items="unknownItems"
				:selectableMode="['checkbox']"
				localStorageId="unknownItemsSearchList"
				selectable
				@selected="onRowsSelected"
			>
				<template v-slot:pictureUrls="{ item }">
					<div class="pictures-container">
						<div v-for="url of item.pictureUrls" :key="url">
							<img :src="url" style="height: 100px; cursor: pointer;" @click="openModal(url)">
						</div>
					</div>
				</template>
			</t-grid>
			<t-no-content-panel v-else :text="$t('noItemsFound')" icon="order" />
			<t-overlay
				:isShown="loading"
				absolute
				loader
			/>
		</div>
		<PictureModal/>
	</t-vertical-panel>
</template>

<script>
import { mapState, mapActions } from './store/unknownItems.store.js';
import Grid from '@/components/evergreen/timpex/grid/TGrid';
import { storageDateFormat, displayDateFormat, dateExists } from '@/core';
import moment from 'moment';
import PictureModal from './PictureModal';

export default {
	name: 'UnknownSearch',
	components: { TGrid: Grid, PictureModal },
	data() {
		return {
			defaultColumnDefinition: {
				resizable: true,
				headerFilterInputWithButton: true,
			},
			rowsSelected: 0
		};
	},
	computed: {
		...mapState([
			'items',
			'loading'
		]),
		columnDefinitions() {
			return [
				{
					field: 'registeredDate.date',
					displayName: this.$t('registeredDate'),
					valueGetter: item => this.dateFormatter(item.registeredDate.date),
					width: 170,
					resizable: false,
					type: 'date',
					dateFormat: displayDateFormat,
					comparableValueGetter: (item) => moment(item.registeredDate.date, storageDateFormat).toISOString()
				},{
					field: 'searchQuery',
					displayName: this.$t('barcode'),
					width: 250,
				},{
					field: 'message',
					displayName: this.$t('message'),
					minWidth: 125,
				},{
					field: 'pictureUrls',
					displayName: this.$t('pictures'),
					minWidth: 125,
				},{
					field: 'editedDate',
					displayName: this.$t('editedDate'),
					valueGetter: item => this.dateFormatter(this.singleDateGetter(item.editedDate)),
					width: 200,
					resizable: false,
					type: 'date',
					comparableValueGetter: item => moment(this.singleDateGetter(item.editedDate), storageDateFormat).startOf('day').toISOString()
				}
			];
		},
		itemsFound() {
			return this.items.length;
		},
		unknownItems() {
			return this.items;
		},
	},
	async mounted() {
		try {
			await this.getItems();
		} catch (e) {
			this.$notify({
				type: 'error',
				title: this.$t('failedToFetch')
			});
		}
	},
	methods: {
		...mapActions([
			'getItems',
			'deleteItems',
			'setModalUrl'
		]),
		dateFormatter(date) {
			if (!date) return '';
			return moment(date, storageDateFormat).format('LLL');
		},
		singleDateGetter(dateObj) {
			return dateExists(dateObj) ? dateObj.date : undefined;
		},
		openModal(url) {
			this.setModalUrl(url);
		},
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		async onDeleteItems() {
			let selectedItems = this.$refs.grid.getSelectedItems();
			if (!selectedItems) return;
			try {
				await this.deleteItems(selectedItems);
				this.$notify({
					type: 'success',
					title: this.$tc('deletedItems', selectedItems.length)
				});
				this.rowsSelected = 0;
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToDelete')
				});
			}
		},
	},
};
</script>


<style lang="scss" scoped>

.pictures-container {
	display: flex;
	width: 100%;
	flex-direction: row;
	justify-content: flex-start;
	overflow: auto;
}

.unknown-list {
	.unknown-list-header {
		height: 100%;
		display: flex;
		align-items: center;
		padding-left: 15px;
	}
	.unknown-list-sub-header {
		display: flex;
		height: 100%;
		align-items: center;
		justify-content: flex-end;

		> button {
			margin: 10px;
		}
	}
	.unknown-list-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px
	}
}
</style>
